import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCandidateTouchpoint,
  fetchCandidateTouchpointDrilldown,
} from './actions';

const constructTableWithoutBreakDown = ({
  data = [],
  headers = [],
  groupby = [],
  groupbyKey = 'dt',
  parent = null,
}) => {
  const currdata = data?.reduce((acc, curr) => {
    const obj = {};
    curr.forEach((element, index) => {
      obj[headers[index]] = element;
    });
    obj['group_by'] = groupby[+obj[groupbyKey]];
    if (parent) {
      obj['parent'] = parent;
    }
    return [...acc, obj];
  }, []);
  return currdata;
};

const constructTableWithBreakDown = ({
  data = [],
  headers = [],
  groupby = [],
  groupbyKey = 'dt',
  breakDown = [],
  breakDownKey = 'g2',
}) => {
  const currdata = data?.reduce((acc, curr) => {
    const obj = {};
    curr.forEach((element, index) => {
      obj[headers[index]] = element;
    });
    obj['group_by'] = groupby[+obj[groupbyKey]];
    obj['subRows'] = constructTableWithoutBreakDown({
      data: curr[0],
      headers: headers,
      groupby: breakDown,
      groupbyKey: breakDownKey,
      parent: groupby[+obj[groupbyKey]],
    });
    return [...acc, obj];
  }, []);
  return currdata;
};

const constructChartData = ({
  compareDate = {},
  data = [],
  headers = [],
  groupBy = [],
}) => {
  const hasCompare = compareDate?.from || compareDate?.to;
  if (!hasCompare) {
    const xdata = data?.reduce(
      (acc, curr) => {
        acc?.contacted.push({ y: curr[4] });
        acc?.opened.push({ y: curr[5] });
        acc?.replied.push({ y: curr[6] });
        acc?.interested.push({ y: curr[7] });
        return acc;
      },
      { contacted: [], opened: [], replied: [], interested: [] }
    );
    return { xaxis: groupBy, ...xdata };
  } else {
    const dataSet = data?.filter((d) => d[0] === '0');
    const cDataSet = data?.filter((d) => d[0] === '1');
    const xdata = dataSet?.reduce(
      (acc, curr, index) => {
        const currCompare = cDataSet[index];
        acc?.contacted.push({ y: curr?.[4] || 0, c: currCompare?.[4] || 0 });
        acc?.opened.push({ y: curr?.[5] || 0, c: currCompare?.[5] || 0 });
        acc?.replied.push({ y: curr?.[6] || 0, c: currCompare?.[6] || 0 });
        acc?.interested.push({ y: curr?.[7] || 0, c: currCompare?.[7] || 0 });
        return acc;
      },
      { contacted: [], opened: [], replied: [], interested: [] }
    );
    return { xaxis: groupBy, ...xdata };
  }
};

const constructDrilldownData = (payload) => {
  const k1Map = payload?.lookups?.k1;
  const k2Map = payload?.lookups?.k2;
  const returnData = payload?.table?.map((data) => {
    const [, k1, k2] = data;
    return {
      ...k1Map[k1],
      activity: k2Map[k2],
    };
  });
  return returnData;
};

const initialState = {
  isLoading: true,
  table: [],
  chart: {},
  drilldownIsLoading: true,
  initialLoading: false,
  drilldownData: [],
  totalCount: 0,
  currentDrillPage: 1,
};

const { reducer, actions } = createSlice({
  name: 'flow-health-candidate-touchpoint',
  initialState,

  reducers: {
    clearDrilldownData: (state) => {
      state.drilldownData = [];
      state.drilldownIsLoading = false;
      state.searchTerm = '';
      state.currentDrillPage = 1;
    },
    applySearch: (state, action) => {
      state.searchTerm = action.payload.searchTerm;
    },
  },

  extraReducers: {
    [fetchCandidateTouchpoint.pending]: (state) => {
      state.isLoading = true;
    },
    [fetchCandidateTouchpoint.fulfilled]: (state, { payload = {}, params }) => {
      const hasBreakDown = payload?.lookups?.g2?.length;
      const { filter_by_date = {}, compare_date = {} } = payload;
      const chartDate = constructChartData({
        compareDate: compare_date,
        data: payload?.chart,
        headers: payload?.headers,
        groupBy: payload?.lookups?.dt,
      });
      state.chart = { ...chartDate, filter_by_date, compare_date };
      if (!hasBreakDown) {
        const chartData = payload?.chart?.filter((d) => d[0] === '0');
        const data = payload?.table?.length ? payload?.table : chartData;
        const headers = payload?.headers;
        const groupby = payload?.table?.length
          ? payload?.lookups?.g1
          : payload?.lookups?.dt;
        const groupbyKey = payload?.table?.length ? 'g1' : 'dt';
        const table = constructTableWithoutBreakDown({
          data,
          headers,
          groupby,
          groupbyKey,
        });
        state.table = table;
      } else {
        const chartData = payload?.chart?.filter((d) => d[0] === '0');
        const data = payload?.table?.length ? payload?.table : chartData;
        const headers = payload?.headers;
        const groupby = payload?.lookups?.g1
          ? payload?.lookups?.g1
          : payload?.lookups?.dt;
        const groupbyKey = payload?.lookups?.g1 ? 'g1' : 'dt';
        const table = constructTableWithBreakDown({
          data,
          headers,
          groupby,
          groupbyKey,
          breakDown: payload?.lookups?.g2,
          breakDownKey: 'g2',
        });
        state.table = table;
      }
      state.data = payload || {};
      state.isLoading = false;
    },

    [fetchCandidateTouchpointDrilldown.pending]: (state, { meta }) => {
      const { page } = meta?.arg || {};
      if (page === 1) {
        state.initialLoading = true;
      } else {
        state.drilldownIsLoading = true;
      }
    },
    [fetchCandidateTouchpointDrilldown.fulfilled]: (
      state,
      { meta, payload = {} }
    ) => {
      state.drilldownIsLoading = false;
      state.initialLoading = false;

      const newData = constructDrilldownData(payload);
      // Get the page from the action metadata
      const page = meta?.arg?.page || 1;
      // Handle pagination - append data for page > 1
      if (page === 1) {
        state.drilldownData = newData;
      } else {
        state.drilldownData = [...state.drilldownData, ...newData];
      }

      state.totalCount = payload?.pagination?.total_count;
      state.currentDrillPage = page;
    },
    [fetchCandidateTouchpointDrilldown.rejected]: (state) => {
      state.isDrillLoading = false;
      state.initialLoading = false;
    },
  },
});
export const { applySearch, clearDrilldownData } = actions;
export default reducer;
